// ═══════════════════════════════════════════════════════════
// ESP Tool – Main Application
// ═══════════════════════════════════════════════════════════

import { ESPLoader, FlashOptions, LoaderOptions, Transport } from "../../../lib";
import { serial } from "web-serial-polyfill";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import CryptoJS from "crypto-js";

const serialLib = !navigator.serial && navigator.usb ? serial : navigator.serial;

// ── DOM References ──────────────────────────────────────────
const baudrates = document.getElementById("baudrates") as HTMLSelectElement;
const connectButton = document.getElementById("connectButton") as HTMLButtonElement;
const disconnectButton = document.getElementById("disconnectButton") as HTMLButtonElement;
const traceButton = document.getElementById("copyTraceButton") as HTMLButtonElement;
const resetButton = document.getElementById("resetButton") as HTMLButtonElement;
const monitorButton = document.getElementById("monitorButton") as HTMLButtonElement;
const consoleStopButton = document.getElementById("consoleStopButton") as HTMLButtonElement;
const eraseButton = document.getElementById("eraseButton") as HTMLButtonElement;
const eraseConfirmButton = document.getElementById("eraseConfirmButton") as HTMLButtonElement;
const addFileButton = document.getElementById("addFile") as HTMLButtonElement;
const programButton = document.getElementById("programButton") as HTMLButtonElement;
const terminalEl = document.getElementById("terminal");
const lblConnTo = document.getElementById("lblConnTo");
const table = document.getElementById("fileTable") as HTMLTableElement;
const debugLogging = document.getElementById("debugLogging") as HTMLInputElement;

// Layout elements
const welcomePanel      = document.getElementById("welcomePanel");
const connectedControls = document.getElementById("connectedControls");
const footerBaud        = document.getElementById("footerBaud");
const footerConnLabel   = document.getElementById("footerConnLabel");

// Sidebar elements
const sidebarChip       = document.getElementById("sidebarChip");
const sidebarChipName   = document.getElementById("sidebarChipName");
const sidebarChipDesc   = document.getElementById("sidebarChipDesc");
const sidebarMac        = document.getElementById("sidebarMac");
const sidebarStatusDot  = document.getElementById("sidebarStatusDot");
const sidebarStatusLabel= document.getElementById("sidebarStatusLabel");
const sidebarBaud       = document.getElementById("sidebarBaud");

// Device info elements
const deviceChip        = document.getElementById("deviceChip");
const deviceChipDesc    = document.getElementById("deviceChipDesc");
const deviceArchBadge   = document.getElementById("deviceArchBadge");
const deviceMac         = document.getElementById("deviceMac");
const deviceFeaturesList= document.getElementById("deviceFeaturesList");
const deviceArch        = document.getElementById("deviceArch");
const deviceCores       = document.getElementById("deviceCores");
const deviceMaxFreq     = document.getElementById("deviceMaxFreq");
const deviceFlashSize   = document.getElementById("deviceFlashSize");
const deviceCrystal     = document.getElementById("deviceCrystal");
const deviceFlashId     = document.getElementById("deviceFlashId");
const deviceFlashMfr    = document.getElementById("deviceFlashMfr");
const deviceBootOffset  = document.getElementById("deviceBootOffset");
const deviceBaud        = document.getElementById("deviceBaud");
const deviceUsb         = document.getElementById("deviceUsb");
const deviceLoading     = document.getElementById("deviceLoading");

// Drag and drop
const dropZone = document.getElementById("dropZone");
const dropZoneContent = document.getElementById("dropZoneContent");
const dropZoneActive = document.getElementById("dropZoneActive");

// Terminal toolbar controls
const termFontDecrease    = document.getElementById("termFontDecrease") as HTMLButtonElement;
const termFontIncrease    = document.getElementById("termFontIncrease") as HTMLButtonElement;
const termFontSizeDisplay = document.getElementById("termFontSizeDisplay");
const termTimestampCb     = document.getElementById("termTimestamp") as HTMLInputElement;
const termAutoScrollCb    = document.getElementById("termAutoScroll") as HTMLInputElement;

// ── Static chip lookup table ───────────────────────────────
const CHIP_STATIC: Record<string, { arch: string; cores: number; maxFreq: string }> = {
  "ESP32":    { arch: "Xtensa LX6", cores: 2, maxFreq: "240 MHz" },
  "ESP32-S2": { arch: "Xtensa LX7", cores: 1, maxFreq: "240 MHz" },
  "ESP32-S3": { arch: "Xtensa LX7", cores: 2, maxFreq: "240 MHz" },
  "ESP32-C2": { arch: "RISC-V",    cores: 1, maxFreq: "120 MHz" },
  "ESP32-C3": { arch: "RISC-V",    cores: 1, maxFreq: "160 MHz" },
  "ESP32-C5": { arch: "RISC-V",    cores: 1, maxFreq: "240 MHz" },
  "ESP32-C6": { arch: "RISC-V",    cores: 1, maxFreq: "160 MHz" },
  "ESP32-C61":{ arch: "RISC-V",    cores: 1, maxFreq: "160 MHz" },
  "ESP32-H2": { arch: "RISC-V",    cores: 1, maxFreq: "96 MHz"  },
  "ESP32-P4": { arch: "RISC-V",    cores: 2, maxFreq: "400 MHz" },
  "ESP8266":  { arch: "Xtensa L106",cores: 1, maxFreq: "160 MHz" },
};

const FLASH_MANUFACTURERS: Record<number, string> = {
  0x01: "Cypress / Spansion", 0x04: "Fujitsu",    0x0B: "XMC",
  0x1C: "EON",               0x1F: "Atmel",       0x20: "Micron / ST",
  0x37: "AMIC",              0x46: "XMC",         0x5E: "Zbit",
  0x68: "BOYA",              0x85: "Puya",        0x9D: "ISSI",
  0xA1: "Fudan Micro",       0xC2: "Macronix",    0xC8: "GigaDevice",
  0xEF: "Winbond",
};

// ── Terminal Setup ──────────────────────────────────────────
const term = new Terminal({ cols: 120, rows: 40, cursorBlink: true, cursorStyle: "block", fontSize: 14, theme: { background: "#1a1b26" } });
term.open(terminalEl);

// ── Terminal Options ─────────────────────────────────────────
let termFontSize = 14;
let showTimestamps = false;
let autoScroll = true;
let timestampLineStart = true; // track whether next byte starts a new line

function setFontSize(size: number) {
  termFontSize = Math.min(24, Math.max(10, size));
  term.options.fontSize = termFontSize;
  if (termFontSizeDisplay) termFontSizeDisplay.textContent = String(termFontSize);
}

function writeToTerminal(data: string | Uint8Array) {
  // Capture scroll position BEFORE write so we can restore it if auto-scroll is off.
  // xterm scrolls to bottom on every write; the write() callback fires after rendering.
  const savedViewportY = term.buffer.active.viewportY;

  const flush = (output: string | Uint8Array) => {
    if (autoScroll) {
      term.write(output, () => term.scrollToBottom());
    } else {
      term.write(output, () => term.scrollToLine(savedViewportY));
    }
  };

  if (!showTimestamps) {
    flush(data);
  } else {
    const str = typeof data === "string" ? data : new TextDecoder().decode(data);
    const now = new Date();
    const ts = `\x1b[2m[${now.toTimeString().slice(0, 8)}]\x1b[0m `;
    let out = "";
    for (const ch of str) {
      if (timestampLineStart) {
        out += ts;
        timestampLineStart = false;
      }
      out += ch;
      if (ch === "\n") timestampLineStart = true;
    }
    flush(out);
  }
}

// ── App State ───────────────────────────────────────────────
type AppState = "idle" | "connected" | "console";
let currentState: AppState = "idle";
let currentTab = "terminal";

let device: any = null;
let transport: Transport;
let chip: string = null;
let esploader: ESPLoader;
let isConsoleClosed = false;

// ── Toast Notifications ─────────────────────────────────────
function showToast(message: string, type: "success" | "error" | "info" = "info", duration = 4000) {
  const container = document.getElementById("toast-container");

  const colors = {
    success: "bg-green-600 text-white",
    error: "bg-red-600 text-white",
    info: "bg-gray-800 text-white dark:bg-gray-700",
  };

  const icons = {
    success: `<svg class="w-4 h-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>`,
    error: `<svg class="w-4 h-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>`,
    info: `<svg class="w-4 h-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>`,
  };

  const toast = document.createElement("div");
  toast.className = `pointer-events-auto flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${colors[type]} transform translate-x-full transition-transform duration-300 ease-out`;
  toast.innerHTML = `${icons[type]}<span>${message}</span>`;

  container.appendChild(toast);

  // Animate in
  requestAnimationFrame(() => {
    toast.classList.remove("translate-x-full");
    toast.classList.add("translate-x-0");
  });

  // Auto-dismiss
  setTimeout(() => {
    toast.classList.remove("translate-x-0");
    toast.classList.add("translate-x-full");
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

// ── Theme Toggle ────────────────────────────────────────────
// function initTheme() {
//   const stored = localStorage.getItem("esp-tool-theme");
//   const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
//   const isDark = stored ? stored === "dark" : prefersDark;

//   document.documentElement.classList.toggle("dark", isDark);
//   updateThemeIcons(isDark);
// }

// function updateThemeIcons(isDark: boolean) {
//   const iconDark = document.getElementById("themeIconDark");
//   const iconLight = document.getElementById("themeIconLight");
//   iconDark.classList.toggle("hidden", !isDark);
//   iconLight.classList.toggle("hidden", isDark);
// }

// themeToggle.onclick = () => {
//   const isDark = document.documentElement.classList.toggle("dark");
//   localStorage.setItem("esp-tool-theme", isDark ? "dark" : "light");
//   updateThemeIcons(isDark);
// };

// initTheme();

// ── Tab Switching ───────────────────────────────────────────
function switchTab(tabName: string) {
  currentTab = tabName;

  // Update sidebar nav buttons
  document.querySelectorAll(".sidebar-nav-btn").forEach((btn) => {
    const isActive = btn.getAttribute("data-tab") === tabName;
    btn.classList.toggle("sidebar-nav-active", isActive);
  });

  // Update panels
  document.querySelectorAll(".tab-panel").forEach((panel) => {
    const panelId = panel.id.replace("panel-", "");
    if (panelId === tabName) {
      panel.classList.remove("hidden");
      panel.classList.add("flex");
    } else {
      panel.classList.add("hidden");
      panel.classList.remove("flex");
    }
  });

  // Re-fit terminal when switching back to it
  if (tabName === "terminal") {
    setTimeout(() => term.refresh(0, term.rows - 1), 50);
    // Auto-start monitor when switching to terminal while in connected state
    if (currentState === "connected") {
      startMonitor();
      return;
    }
  }
}

document.querySelectorAll(".sidebar-nav-btn").forEach((btn) => {
  (btn as HTMLButtonElement).onclick = () => {
    switchTab(btn.getAttribute("data-tab"));
  };
});

// ── UI State Management ─────────────────────────────────────
function setConnectionStatus(connected: boolean, label?: string) {
  const footerDot = document.getElementById("connected-status");
  footerDot.classList.toggle("bg-red-500", !connected);
  footerDot.classList.toggle("bg-green-500", connected);

  sidebarStatusDot.classList.toggle("bg-red-500", !connected);
  sidebarStatusDot.classList.toggle("bg-green-500", connected);

  const text = label || (connected ? "Connected" : "Disconnected");
  footerConnLabel.textContent = text;
  sidebarStatusLabel.textContent = text;
}

/** Swap between hidden ↔ inline-flex so both display utilities never coexist */
function showBtn(btn: HTMLElement, visible: boolean) {
  if (visible) {
    btn.classList.remove("hidden");
    btn.classList.add("inline-flex");
  } else {
    btn.classList.add("hidden");
    btn.classList.remove("inline-flex");
  }
}

function enterState(state: AppState) {
  currentState = state;

  const isIdle = state === "idle";
  const isConnected = state === "connected";
  const isConsole = state === "console";

  // Welcome panel (shown when idle) - hide all tab panels when idle
  welcomePanel.classList.toggle("hidden", !isIdle);
  welcomePanel.classList.toggle("flex", isIdle);
  if (isIdle) {
    document.querySelectorAll(".tab-panel").forEach((panel) => {
      panel.classList.add("hidden");
      panel.classList.remove("flex");
    });
  }

  // Sidebar: show chip info only when connected
  sidebarChip.classList.toggle("hidden", isIdle);

  // Sidebar nav buttons: disabled when idle
  document.querySelectorAll(".sidebar-nav-btn").forEach((btn) => {
    (btn as HTMLButtonElement).disabled = isIdle;
    btn.classList.toggle("opacity-40", isIdle);
    btn.classList.toggle("pointer-events-none", isIdle);
  });

  // Connected controls container (trace, erase, reset)
  if (isConnected || isConsole) {
    connectedControls.classList.remove("hidden");
    connectedControls.classList.add("flex");
  } else {
    connectedControls.classList.add("hidden");
    connectedControls.classList.remove("flex");
  }

  showBtn(traceButton, isConnected);
  showBtn(eraseButton, isConnected);
  showBtn(monitorButton, isConnected);
  showBtn(resetButton, isConsole);

  showBtn(connectButton, isIdle);
  showBtn(disconnectButton, isConnected);
  showBtn(consoleStopButton, isConsole);

  // Footer / header status
  if (isIdle) {
    lblConnTo.classList.add("hidden");
    document.getElementById("footerTagline")?.classList.remove("hidden");
    footerBaud.classList.add("hidden");
    sidebarBaud.classList.add("hidden");
    setConnectionStatus(false);
  }

  debugLogging.disabled = false; // always editable via settings dropdown

  if (!isIdle) {
    switchTab("device");
  }
}

// ── Settings dropdown toggle ────────────────────────────────
const settingsToggle = document.getElementById("settingsToggle");
const settingsDropdown = document.getElementById("settingsDropdown");

settingsToggle.addEventListener("click", (e) => {
  e.stopPropagation();
  settingsDropdown.classList.toggle("hidden");
});

document.addEventListener("click", (e) => {
  if (!settingsDropdown.contains(e.target as Node) && e.target !== settingsToggle) {
    settingsDropdown.classList.add("hidden");
  }
});

termFontDecrease.onclick = () => setFontSize(termFontSize - 1);
termFontIncrease.onclick = () => setFontSize(termFontSize + 1);
termTimestampCb.onchange = () => { showTimestamps = termTimestampCb.checked; };
termAutoScrollCb.onchange = () => { autoScroll = termAutoScrollCb.checked; };

// ── ESP Loader Terminal Adapter ─────────────────────────────
const espLoaderTerminal = {
  clean() {
    term.clear();
  },
  writeLine(data: string) {
    term.writeln(data);
  },
  write(data: string) {
    term.write(data);
  },
};

// ── File Handling ───────────────────────────────────────────
function handleFileSelect(evt: Event) {
  const input = evt.target as HTMLInputElement & { data?: string };
  const file = input.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (ev: ProgressEvent<FileReader>) => {
    input.data = ev.target.result as string;
  };
  reader.readAsBinaryString(file);
}

// ── Add File Row ────────────────────────────────────────────
function addFileRow(fileName?: string, fileData?: string) {
  const rowCount = table.rows.length;
  const row = table.insertRow(rowCount);

  // Column 1 - Offset
  const cell1 = row.insertCell(0);
  cell1.className = "pr-3 py-1.5";
  const offsetInput = document.createElement("input");
  offsetInput.type = "text";
  offsetInput.id = "offset" + rowCount;
  offsetInput.value = "0x1000";
  offsetInput.className = "w-full text-sm bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500";
  cell1.appendChild(offsetInput);

  // Column 2 - File selector
  const cell2 = row.insertCell(1);
  cell2.className = "pr-3 py-1.5";
  const fileInput = document.createElement("input") as HTMLInputElement & { data?: string };
  fileInput.type = "file";
  fileInput.id = "selectFile" + rowCount;
  fileInput.name = "selected_File" + rowCount;
  fileInput.accept = ".bin,.img,.elf";
  fileInput.addEventListener("change", handleFileSelect, false);
  fileInput.className = "w-full text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 dark:file:bg-gray-700 dark:file:text-gray-300 hover:file:bg-blue-100";
  cell2.appendChild(fileInput);

  // If file data was provided (drag-and-drop), set it
  if (fileData && fileName) {
    fileInput.data = fileData;
    // Show file name in a label since we can't set file input value
    const label = document.createElement("span");
    label.className = "block text-xs text-gray-500 dark:text-gray-400 mt-1";
    label.textContent = fileName;
    cell2.appendChild(label);
  }

  // Column 3 - Progress
  const cell3 = row.insertCell(2);
  cell3.className = "py-1.5 text-center";
  cell3.style.display = "none";
  const progress = document.createElement("div");
  progress.className = "w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2";
  progress.innerHTML = `<div class="bg-blue-600 h-2 rounded-full transition-all duration-200" style="width: 0%"></div>`;
  cell3.appendChild(progress);

  // Column 4 - Remove
  const cell4 = row.insertCell(3);
  cell4.className = "py-1.5 text-center";
  if (rowCount > 1) {
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.title = "Remove file";
    removeBtn.className = "p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors";
    removeBtn.innerHTML = `<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"/></svg>`;
    removeBtn.onclick = () => {
      const idx = Array.from(table.rows).indexOf(row);
      table.deleteRow(idx);
    };
    cell4.appendChild(removeBtn);
  }
}

addFileButton.onclick = () => addFileRow();

// ── Drag and Drop ───────────────────────────────────────────
function setupDragAndDrop() {
  let dragCounter = 0;

  dropZone.addEventListener("dragenter", (e) => {
    e.preventDefault();
    dragCounter++;
    dropZoneContent.classList.add("hidden");
    dropZoneActive.classList.remove("hidden");
    dropZoneActive.classList.add("flex");
  });

  dropZone.addEventListener("dragleave", (e) => {
    e.preventDefault();
    dragCounter--;
    if (dragCounter === 0) {
      dropZoneContent.classList.remove("hidden");
      dropZoneActive.classList.add("hidden");
      dropZoneActive.classList.remove("flex");
    }
  });

  dropZone.addEventListener("dragover", (e) => {
    e.preventDefault();
  });

  dropZone.addEventListener("drop", (e) => {
    e.preventDefault();
    dragCounter = 0;
    dropZoneContent.classList.remove("hidden");
    dropZoneActive.classList.add("hidden");
    dropZoneActive.classList.remove("flex");

    const files = e.dataTransfer?.files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.name.match(/\.(bin|img|elf)$/i)) {
        showToast(`Skipped ${file.name} - only .bin, .img, .elf files`, "error");
        continue;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        addFileRow(file.name, ev.target.result as string);
        showToast(`Added ${file.name}`, "success", 2000);
      };
      reader.readAsBinaryString(file);
    }
  });
}

setupDragAndDrop();

// ── Connect ─────────────────────────────────────────────────
connectButton.onclick = async () => {
  try {
    if (device === null) {
      device = await serialLib.requestPort({});
      transport = new Transport(device, true);
    }

    const loaderOptions = {
      transport,
      baudrate: parseInt(baudrates.value),
      terminal: espLoaderTerminal,
      debugLogging: debugLogging.checked,
    } as LoaderOptions;

    esploader = new ESPLoader(loaderOptions);
    chip = await esploader.main();

    console.log("Settings done for: " + chip);

    // Update UI state
    enterState("connected");
    setConnectionStatus(true, chip);

    // Update footer
    lblConnTo.textContent = chip;
    lblConnTo.classList.remove("hidden");
    document.getElementById("footerTagline")?.classList.add("hidden");
    footerBaud.textContent = baudrates.value + " baud";
    footerBaud.classList.remove("hidden");

    // Set basic info immediately
    deviceChip.textContent = chip || "-";
    deviceBaud.textContent = parseInt(baudrates.value).toLocaleString() + " bps";

    showToast(`Connected to ${chip}`, "success");

    // Fetch detailed device info in background
    populateDeviceInfo();

  } catch (e) {
    console.error(e);
    term.writeln(`Error: ${e.message}`);
    showToast(`Connection failed: ${e.message}`, "error", 6000);
  }
};

// ── Populate device info panel ─────────────────────────────
async function populateDeviceInfo() {
  deviceLoading.classList.remove("hidden");
  deviceLoading.classList.add("flex");
  try {
    const rom = esploader.chip;

    // Chip description & variant
    const desc = await rom.getChipDescription(esploader);
    deviceChipDesc.textContent = desc;

    // Architecture badge + static per-chip data
    const chipName = rom.CHIP_NAME;
    const staticInfo = CHIP_STATIC[chipName];
    if (staticInfo) {
      deviceArch.textContent = staticInfo.arch;
      deviceCores.textContent = staticInfo.cores === 1 ? "1 (Single)" : `${staticInfo.cores} (Dual)`;
      deviceMaxFreq.textContent = staticInfo.maxFreq;
      deviceArchBadge.textContent = staticInfo.arch;
      deviceArchBadge.classList.remove("hidden");
      deviceArchBadge.classList.add("inline-flex");
    }

    // MAC address
    const mac = await rom.readMac(esploader);
    deviceMac.textContent = mac;

    // Features as coloured badges
    const features = await rom.getChipFeatures(esploader);
    deviceFeaturesList.innerHTML = features
      .map((f) => `<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">${f}</span>`)
      .join("");

    // Crystal frequency
    const xtal = await rom.getCrystalFreq(esploader);
    deviceCrystal.textContent = xtal + " MHz";

    // Bootloader offset
    const offset = rom.BOOTLOADER_FLASH_OFFSET;
    deviceBootOffset.textContent = `0x${offset.toString(16).toUpperCase()}`;

    // Flash ID → size + manufacturer
    try {
      const flashIdRaw = await esploader.readFlashId();
      const mfrByte = (flashIdRaw >> 16) & 0xff;
      const capacityByte = flashIdRaw & 0xff;
      const hexId = `0x${flashIdRaw.toString(16).toUpperCase().padStart(6, "0")}`;
      deviceFlashId.textContent = hexId;
      const sizeStr = (esploader as any).DETECTED_FLASH_SIZES?.[capacityByte];
      deviceFlashSize.textContent = sizeStr || "?";
      deviceFlashMfr.textContent =
        FLASH_MANUFACTURERS[mfrByte] || `Unknown (0x${mfrByte.toString(16).toUpperCase()})`;
    } catch {
      // Flash ID read can fail on some configs - leave as -
    }

    // USB connection type (if available from port info)
    try {
      const portInfo = (device as any).getInfo?.();
      if (portInfo?.usbVendorId) {
        deviceUsb.textContent = `VID 0x${portInfo.usbVendorId.toString(16).toUpperCase()} / PID 0x${portInfo.usbProductId.toString(16).toUpperCase()}`;
      }
    } catch {
      // ignore
    }

    // Update sidebar with chip description + mac (reuse already-fetched values)
    sidebarChipDesc.textContent = desc;
    sidebarMac.textContent = mac;

  } catch (e) {
    console.warn("Device info fetch error:", e);
  } finally {
    deviceLoading.classList.add("hidden");
    deviceLoading.classList.remove("flex");
  }
}

// ── Disconnect ──────────────────────────────────────────────
disconnectButton.onclick = async () => {
  if (transport) await transport.disconnect();
  term.reset();
  enterState("idle");
  cleanUp();
  showToast("Disconnected", "info", 2000);
};

// ── Trace ───────────────────────────────────────────────────
traceButton.onclick = async () => {
  if (transport) {
    transport.returnTrace();
    showToast("Trace copied", "success", 2000);
  }
};

// ── Reset ─────────────────────────────────────────────────
resetButton.onclick = async () => {
  if (transport) {
    // DTR → IO0 (keep HIGH = normal boot, not bootloader)
    // RTS → EN  (pull LOW to reset, then release)
    await transport.setDTR(false);  // IO0 = HIGH
    await transport.setRTS(true);   // EN  = LOW  (assert reset)
    await new Promise((resolve) => setTimeout(resolve, 100));
    await transport.setRTS(false);  // EN  = HIGH (release reset)
    showToast("ESP reset", "info", 2000);
  }
};

// ── Serial Monitor ──────────────────────────────────────
const monitorEncoder = new TextEncoder();
let monitorInputDispose: { dispose: () => void } | null = null;

const serialInputBar = document.getElementById("serialInputBar");
const serialInput    = document.getElementById("serialInput") as HTMLInputElement;
const serialSendBtn  = document.getElementById("serialSendBtn") as HTMLButtonElement;

async function serialSend(text: string) {
  if (!text || !device?.writable) return;
  const writer = device.writable.getWriter();
  try {
    await writer.write(monitorEncoder.encode(text + "\n"));
  } finally {
    writer.releaseLock();
  }
}

serialSendBtn.onclick = async () => {
  await serialSend(serialInput.value);
  serialInput.value = "";
  serialInput.focus();
};

serialInput.addEventListener("keydown", async (e: KeyboardEvent) => {
  if (e.key === "Enter") {
    await serialSend(serialInput.value);
    serialInput.value = "";
  }
});

async function startMonitor() {
  try {
    timestampLineStart = true; // reset timestamp state on (re)connect
    // Close the ESPLoader connection so we can reopen at monitor baud
    if (transport) {
      await transport.disconnect();
    }

    // Reconnect the same device at the configured baud rate
    transport = new Transport(device, true);
    const monitorBaud = parseInt(baudrates.value);
    await transport.connect(monitorBaud);

    enterState("console");
    setConnectionStatus(true, chip ? `${chip} - Monitor` : "Monitor");
    sidebarBaud.textContent = monitorBaud.toLocaleString() + " bps";
    sidebarBaud.classList.remove("hidden");

    // Reset chip so user firmware runs (IO0 high = normal boot, RTS toggles EN)
    await transport.setDTR(false);  // IO0 = HIGH
    await transport.setRTS(true);   // EN  = LOW
    await new Promise((resolve) => setTimeout(resolve, 100));
    await transport.setRTS(false);  // EN  = HIGH

    isConsoleClosed = false;
    switchTab("terminal");
    showToast("Serial monitor started - type to send", "info", 2000);

    // Show input bar
    serialInputBar.classList.remove("hidden");
    serialInputBar.classList.add("flex");
    serialInput.focus();

    // Wire keyboard input → raw serial write (bypass SLIP encoding)
    if (monitorInputDispose) monitorInputDispose.dispose();
    monitorInputDispose = term.onData(async (data: string) => {
      if (currentState !== "console" || !device?.writable) return;
      const writer = device.writable.getWriter();
      try {
        await writer.write(monitorEncoder.encode(data));
      } finally {
        writer.releaseLock();
      }
    });

    // Focus terminal so keypresses are captured immediately
    term.focus();

    // Read loop
    while (!isConsoleClosed) {
      const readLoop = transport.rawRead();
      const { value, done } = await readLoop.next();
      if (done || !value) break;
      writeToTerminal(value);
    }

    monitorInputDispose?.dispose();
    monitorInputDispose = null;
    serialInputBar.classList.add("hidden");
    serialInputBar.classList.remove("flex");
    serialInput.value = "";

    if (!isConsoleClosed) {
      enterState("idle");
      cleanUp();
    }
  } catch (e) {
    console.error(e);
    term.writeln(`\x1b[31mMonitor error: ${e.message}\x1b[0m`);
    showToast(`Monitor error: ${e.message}`, "error");
    monitorInputDispose?.dispose();
    monitorInputDispose = null;
    serialInputBar.classList.add("hidden");
    serialInputBar.classList.remove("flex");
    serialInput.value = "";
    enterState("idle");
    cleanUp();
  }
}

monitorButton.onclick = () => startMonitor();

// ── Device panel CTAs ───────────────────────────────────────
document.getElementById("ctaFlash")?.addEventListener("click", () => switchTab("flash"));
document.getElementById("ctaMonitor")?.addEventListener("click", () => switchTab("terminal"));

// ── Erase Flash ─────────────────────────────────────────────
eraseConfirmButton?.addEventListener("click", async () => {
  try {
    showToast("Erasing flash…", "info", 10000);
    await esploader.eraseFlash();
    showToast("Flash erased successfully", "success");
  } catch (e) {
    console.error(e);
    term.writeln(`Error: ${e.message}`);
    showToast(`Erase failed: ${e.message}`, "error", 6000);
  }
});

// ── Stop Monitor ────────────────────────────────────────────
consoleStopButton.onclick = async () => {
  isConsoleClosed = true;
  monitorInputDispose?.dispose();
  monitorInputDispose = null;
  serialInputBar.classList.add("hidden");
  serialInputBar.classList.remove("flex");
  serialInput.value = "";
  if (transport) {
    await transport.disconnect();
    await transport.waitForUnlock(1500);
  }
  term.reset();
  enterState("idle");
  cleanUp();
  showToast("Monitor stopped", "info", 2000);
};

// ── Cleanup ─────────────────────────────────────────────────
function cleanUp() {
  device = null;
  transport = null;
  chip = null;

  // Reset device info panel
  const dash = "-";
  deviceChip.textContent = dash;
  deviceChipDesc.textContent = dash;
  deviceMac.textContent = dash;
  deviceFeaturesList.innerHTML = `<span class="text-sm text-gray-400 dark:text-gray-500">-</span>`;
  deviceArch.textContent = dash;
  deviceCores.textContent = dash;
  deviceMaxFreq.textContent = dash;
  deviceFlashSize.textContent = dash;
  deviceCrystal.textContent = dash;
  deviceFlashId.textContent = dash;
  deviceFlashMfr.textContent = dash;
  deviceBootOffset.textContent = dash;
  deviceBaud.textContent = dash;
  deviceUsb.textContent = dash;
  deviceArchBadge.classList.add("hidden");
  deviceArchBadge.classList.remove("inline-flex");

  // Reset sidebar chip info
  sidebarChipName.textContent = "";
  sidebarChipDesc.textContent = "";
  sidebarMac.textContent = "";
}

// ── Program Button Disable/Enable ───────────────────────────
function disableButton(element: HTMLButtonElement, disable: boolean) {
  element.disabled = disable;
  element.classList.toggle("opacity-50", disable);
  element.classList.toggle("cursor-not-allowed", disable);
  element.classList.toggle("pointer-events-none", disable);
}

// ── Validate Program Inputs ─────────────────────────────────
function validateProgramInputs(): string {
  const offsetArr: number[] = [];
  const rowCount = table.rows.length;

  for (let index = 1; index < rowCount; index++) {
    const row = table.rows[index];
    const offSetObj = row.cells[0].childNodes[0] as HTMLInputElement;
    const offset = parseInt(offSetObj.value);

    if (Number.isNaN(offset)) return `Offset in row ${index} is not a valid address`;
    if (offsetArr.includes(offset)) return `Offset in row ${index} is already in use`;
    offsetArr.push(offset);

    const fileObj = row.cells[1].childNodes[0] as HTMLInputElement & { data?: string };
    if (!fileObj.data) return `No file selected for row ${index}`;
  }
  return "success";
}

// ── Program ─────────────────────────────────────────────────
programButton.onclick = async () => {
  const err = validateProgramInputs();

  if (err !== "success") {
    showToast(err, "error", 5000);
    return;
  }

  disableButton(programButton, true);
  disableButton(addFileButton, true);
  table.style.pointerEvents = "none";

  const fileArray: { data: string; address: number }[] = [];
  const progressBars: HTMLElement[] = [];

  for (let index = 1; index < table.rows.length; index++) {
    const row = table.rows[index];
    const offSetObj = row.cells[0].childNodes[0] as HTMLInputElement;
    const offset = parseInt(offSetObj.value);
    const fileObj = row.cells[1].childNodes[0] as HTMLInputElement & { data: string };

    // Show progress bar
    row.cells[2].style.display = "table-cell";
    row.cells[3].style.display = "none";
    const progressBarInner = row.cells[2].querySelector(".bg-blue-600") as HTMLElement;
    progressBarInner.style.width = "0%";
    progressBars.push(progressBarInner);

    fileArray.push({ data: fileObj.data, address: offset });
  }

  let flashedOk = false;
  try {
    showToast("Flashing…", "info", 30000);

    const flashOptions: FlashOptions = {
      fileArray,
      flashSize: "keep",
      eraseAll: false,
      compress: true,
      reportProgress: (fileIndex: number, written: number, total: number) => {
        const pct = Math.round((written / total) * 100);
        progressBars[fileIndex].style.width = pct + "%";
      },
      calculateMD5Hash: (image: string) => CryptoJS.MD5(CryptoJS.enc.Latin1.parse(image)),
    } as FlashOptions;

    await esploader.writeFlash(flashOptions);
    await esploader.after();
    showToast("Flashed successfully! Starting monitor…", "success", 4000);
    flashedOk = true;
  } catch (e) {
    console.error(e);
    term.writeln(`Error: ${e.message}`);
    showToast(`Flash failed: ${e.message}`, "error", 8000);
  } finally {
    for (let index = 1; index < table.rows.length; index++) {
      table.rows[index].cells[2].style.display = "none";
      table.rows[index].cells[3].style.display = "table-cell";
    }
    disableButton(programButton, false);
    disableButton(addFileButton, false);
    table.style.pointerEvents = "all";

    // Auto-start serial monitor after successful flash
    if (flashedOk) {
      startMonitor();
    }
  }
};

// ── Initialize ──────────────────────────────────────────────
document.getElementById("connectButtonWelcome")?.addEventListener("click", () => connectButton.click());
enterState("idle");
addFileRow(); // Add initial file row
